import * as React from "react";
import { Link, useStaticQuery, graphql } from "gatsby";
import { FiPhone, FiCheckCircle, FiChevronRight } from "react-icons/fi";
import { ImWhatsapp } from "react-icons/im";
import { FaStar } from "react-icons/fa";
import "twin.macro";

import Layout from "../components/layout";
import SEO from "../components/seo";

export default function LocationTemplate({ pageContext }) {
  const { location, allLocations } = pageContext;
  const { site } = useStaticQuery(query);
  const { tel } = site.siteMetadata;

  const nearbyLocations = allLocations
    .filter((l) => l.slug !== location.slug)
    .slice(0, 6);

  return (
    <Layout>
      <SEO
        title={`Garten- und Landschaftsbau ${location.name}`}
        description={`A & F Garten- und Landschaftsbau in ${location.name} – Heckenschnitt, Rasenmähen, Pflasterarbeiten und mehr. Jetzt Angebot einholen: ${tel}`}
      />

      {/* Hero */}
      <section tw="container mx-auto px-6 py-16">
        <div tw="max-w-3xl">
          <p tw="text-primary font-medium mb-2">
            Flensburg · {location.region} · {location.distance}
          </p>
          <h1 tw="text-4xl md:text-5xl font-medium leading-tight text-secondary">
            Garten- und Landschaftsbau{" "}
            <span tw="text-primary">{location.name}</span>
          </h1>
          <p tw="mt-6 text-xl leading-relaxed text-gray-700">
            {location.intro}
          </p>

          <div tw="mt-6 flex flex-wrap items-center gap-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <FaStar key={i} size={20} color="#f59e0b" />
            ))}
            <span tw="ml-2 font-semibold text-secondary">5,0</span>
            <span tw="ml-1 text-gray-500 text-sm">· Google Bewertungen</span>
          </div>

          <div tw="mt-8 flex flex-wrap gap-3">
            <a
              tw="text-lg hover:bg-opacity-90 py-3 px-6 rounded-md inline-flex items-center font-medium bg-primary text-white"
              href={"tel:" + tel}
            >
              <FiPhone size={22} tw="mr-2" /> Jetzt anrufen
            </a>
            <a
              tw="text-lg hover:bg-opacity-90 py-3 px-6 rounded-md inline-flex items-center font-medium bg-primary text-white"
              href={"whatsapp://send?phone=" + tel.replace(/([^0-9])+/g, "")}
            >
              <ImWhatsapp size={22} tw="mr-2" /> WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Services */}
      <section tw="bg-gray-50 py-16">
        <div tw="container mx-auto px-6">
          <h2 tw="text-3xl md:text-4xl font-medium text-secondary mb-10">
            Unsere Leistungen in{" "}
            <span tw="text-primary">{location.name}</span>
          </h2>
          <ul tw="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
            {location.services.map((service) => (
              <li key={service} tw="flex items-start gap-3 text-lg">
                <FiCheckCircle
                  size={22}
                  tw="text-primary flex-shrink-0 mt-0.5"
                />
                <span>{service}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Why us */}
      <section tw="container mx-auto px-6 py-16 max-w-3xl">
        <h2 tw="text-3xl font-medium text-secondary mb-8">
          Warum A &amp; F in <span tw="text-primary">{location.name}</span>?
        </h2>
        <div tw="space-y-6 text-lg text-gray-700 leading-relaxed">
          <p>
            Als lokales Unternehmen aus Flensburg sind wir schnell in{" "}
            {location.name} – keine langen Anfahrten, keine überhöhten Preise.
            Wir kennen die Region und die Anforderungen der Gärten hier.
          </p>
          <p>
            Asmar und sein Team stehen für persönlichen Kontakt und
            handwerkliche Sorgfalt. Sie bekommen keine anonyme Firma, sondern
            einen direkten Ansprechpartner, der zu Ihnen kommt, zuhört und dann
            erst loslegt.
          </p>
          <p>
            <strong>Kein Angebot ohne Besichtigung.</strong> Wir schauen uns Ihr
            Projekt vor Ort an und erstellen ein faires, individuelles Angebot –
            kostenlos und unverbindlich.
          </p>
        </div>
        <div tw="mt-10 p-6 border-l-4 border-primary bg-gray-50 rounded-r-xl">
          <p tw="text-xl font-medium text-secondary">
            Kostenlose Beratung &amp; Angebot in {location.name}
          </p>
          <p tw="mt-2 text-gray-600">
            Rufen Sie uns an oder schreiben Sie uns auf WhatsApp – wir melden
            uns innerhalb von 24 Stunden.
          </p>
          <div tw="mt-4 flex flex-wrap gap-3">
            <a
              tw="py-2 px-5 rounded-md inline-flex items-center font-medium bg-primary text-white hover:bg-opacity-90"
              href={"tel:" + tel}
            >
              <FiPhone size={18} tw="mr-2" /> {tel}
            </a>
            <a
              tw="py-2 px-5 rounded-md inline-flex items-center font-medium bg-primary text-white hover:bg-opacity-90"
              href={"whatsapp://send?phone=" + tel.replace(/([^0-9])+/g, "")}
            >
              <ImWhatsapp size={18} tw="mr-2" /> WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Nearby locations */}
      <section tw="bg-gray-50 py-12">
        <div tw="container mx-auto px-6">
          <h3 tw="text-xl font-medium text-secondary mb-6">
            Auch in diesen Orten tätig:
          </h3>
          <div tw="flex flex-wrap gap-3">
            {nearbyLocations.map((l) => (
              <Link
                key={l.slug}
                to={`/garten-und-landschaftsbau-${l.slug}/`}
                tw="inline-flex items-center gap-1 text-primary hover:underline font-medium"
              >
                <FiChevronRight size={16} />
                {l.name}
              </Link>
            ))}
            <Link
              to="/"
              tw="inline-flex items-center gap-1 text-gray-500 hover:text-primary hover:underline"
            >
              <FiChevronRight size={16} />
              Alle Standorte
            </Link>
          </div>
        </div>
      </section>

      {/* Back to main */}
      <div tw="container mx-auto px-6 py-8">
        <Link to="/" tw="text-primary hover:underline font-medium inline-flex items-center gap-1">
          ← Zurück zur Startseite
        </Link>
      </div>
    </Layout>
  );
}

const query = graphql`
  query LocationTemplate {
    site {
      siteMetadata {
        tel
      }
    }
  }
`;
